import React, { useEffect, useState } from "react";
import Modal from "../components/Modal/Modal";
import styles from "./ImprovementProjectsModal.module.scss";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "../redux/modalStore";
import Table from "../components/Table/Table";
import SecondaryButton from "../components/Button/SecondaryButton";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Button from "../components/Button/Button";
import { Field, Form, Formik } from "formik";
import { ReactComponent as EditIcon } from "../icons/edit.svg";
import Select from "../components/Select/Select";
import Pill from "../components/Pill/Pill";
import Textarea from "../components/Textarea/Textarea";
import { getOrganizationData } from "../actions/organization";
import axiosInstance from "../utils/utils";
import * as Yup from "yup";
import Input from "../components/Input/Input";
import ModalSideBar from "../components/ModalSideBar/ModalSideBar";
import ConfirmationPopup from "./ConfirmationPopup";
import { formatDate, stripHtmlTags } from "../utils/helpers";
import Separator from "../components/Separator/Separator";
import WYSIWYGEditor from "../components/WYSIWYGEditor/WYSIWYGEditor";
import DOMPurify from "dompurify";
import DatePickerField from "../components/FormikComponents/DatePickerField";
import { ReactComponent as LockIcon } from "../icons/lock.svg";
import { ReactComponent as BackArrow } from "../icons/back-arrow.svg";
import { marked } from "marked";
import WarningIllustration from "../icons/no-data.svg";
import IconSelect from "../components/IconSelect/IconSelect";
import ClosedLockIcon from "../icons/lock.svg";
import PublicIcon from "../icons/public.svg";
import {
  clearModalHistory,
  popModal,
  pushModal,
} from "../redux/modalNavigationStore";

const ImprovementsProjectsModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { improvementKey } = useParams();
  const sessionToken = localStorage.getItem("session_token");
  const location = useLocation();

  const { linkedData } = useSelector((state) => state.organization);
  const user = useSelector((state) => state.user.user);
  const modalHistory = useSelector((state) => state.modalNavigation.history);
  const openedModal = useSelector((state) => state.modal.openedModal);
  const improvementProjectsData = openedModal?.data;

  const [editField, setEditField] = useState(null);
  const [editableData, setEditableData] = useState(
    improvementProjectsData || {},
  );
  const [unsavedChanges, setUnsavedChanges] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [updates, setUpdates] = useState(
    improvementProjectsData?.updates || [],
  );
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  useEffect(() => {
    setEditableData(improvementProjectsData || {});
    setUnsavedChanges({});
    setEditField(null);
    setLoading(false);
    setUpdates(improvementProjectsData?.updates || []);
  }, [improvementProjectsData?.id]);

  useEffect(() => {
    // Add the event listener when the component mounts
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup the event listener when the component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!improvementKey) {
      dispatch(hideModal());
    }
  }, [improvementKey, dispatch]);

  // Extract unique risks from linked findings
  const linkedRisks = Array.isArray(linkedData?.linked_findings)
    ? linkedData.linked_findings
        .map((finding) => finding.risk) // take the risk object
        .filter((risk) => risk && risk.risk_id) // make sure risk exists
        .filter(
          (risk, index, self) =>
            self.findIndex((r) => r.risk_id === risk.risk_id) === index,
        ) // unique risks based on risk_id
    : [];

  const handleOnClose = (dirty, resetForm) => {
    if (dirty) {
      setShowConfirmationPopup(true);
    } else {
      dispatch(hideModal());
      dispatch(clearModalHistory());
      navigate(`/${orgId}/improvement-projects${location.search}`, {
        replace: true,
      });
      if (resetForm && typeof resetForm === "function") {
        resetForm();
      }
      setEditableData(improvementProjectsData || {});
      setEditField(null);
    }
  };

  const handleConfirmClose = (resetForm) => {
    setShowConfirmationPopup(false);

    navigate(`/${orgId}/improvement-projects${location.search}`, {
      replace: true,
    });

    dispatch(hideModal());
    dispatch(clearModalHistory());
    setEditField(null);
    resetForm();
  };

  const handleCancel = () => {
    setShowConfirmationPopup(false);
  };

  const handleFieldChange = (field, value) => {
    setUnsavedChanges((prevChanges) => ({
      ...prevChanges,
      [field]: value,
    }));
    setEditableData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleLinkedModalOpen = (path) => {
    dispatch(pushModal(window.location.pathname));
    dispatch(hideModal());
    navigate(`/${orgId}/${path}`);
  };

  const handleGoBack = () => {
    if (modalHistory.length > 0) {
      const lastPath = modalHistory[modalHistory.length - 1];
      dispatch(popModal());
      navigate(lastPath);
    } else {
      navigate(-1);
    }
  };

  const handleSave = async (values, resetForm) => {
    // Define a helper function to sanitize the data and handle undefined, null, or empty values
    const sanitizeData = (data) => {
      return Object.keys(data).reduce((acc, key) => {
        if (Array.isArray(data[key])) {
          acc[key] =
            data[key].filter((item) =>
              typeof item === "string" ? item.trim() !== "" : item,
            ) || [];
        } else if (typeof data[key] === "string") {
          acc[key] = data[key].trim();
        } else if (key === "date" && data[key] instanceof Date) {
          // Date is in "YYYY-MM-DD" format
          acc[key] = data[key].toISOString().split("T")[0];
        } else {
          acc[key] = data[key] ?? "";
        }
        return acc;
      }, {});
    };

    // Sanitize both the form values and the original improvementProjectsData for comparison
    const sanitizedValues = sanitizeData(values);
    const sanitizedOriginalData = sanitizeData(improvementProjectsData);

    // Detect changed fields
    const changedFields = {};
    for (const key in sanitizedValues) {
      if (
        Array.isArray(sanitizedValues[key]) ||
        typeof sanitizedValues[key] === "object"
      ) {
        // Compare arrays or objects by their JSON string representations
        if (
          JSON.stringify(sanitizedValues[key]) !==
          JSON.stringify(sanitizedOriginalData[key])
        ) {
          changedFields[key] = sanitizedValues[key];
        }
      } else {
        // Compare primitive values directly
        if (sanitizedValues[key] !== sanitizedOriginalData[key]) {
          changedFields[key] = sanitizedValues[key];
        }
      }
    }

    // If no changes detected, exit early
    if (Object.keys(changedFields).length === 0) {
      return; // No changes to save
    }

    const payload = {
      id: improvementProjectsData?.id, // Use the existing improvement project ID
      ...changedFields, // Include only changed fields
    };

    setLoading(true);

    try {
      // Make the PATCH request to update the improvement project data
      const response = await axiosInstance.patch(
        `/nocodb/improvements?org_id=${orgId}`,
        [payload], // Send an array with the payload
        {
          headers: {
            "Content-Type": "application/json",
            "session-token": sessionToken,
          },
        },
      );

      setEditableData((prevData) => ({
        ...prevData,
        ...changedFields,
      }));

      setUnsavedChanges({});
      setEditField(null);

      // If the improvement status is set to "Done", update the linked findings
      if (sanitizedValues.status === "Done") {
        const linkedFindings = linkedData?.linked_findings || [];

        if (linkedFindings.length > 0) {
          // Patch each linked finding to set its status to "Done"
          const updateFindingsPromises = linkedFindings.map((findingId) => {
            return axiosInstance.patch(
              `/nocodb/findings?org_id=${orgId}`,
              [
                {
                  id: findingId.id,
                  status: "Done",
                },
              ],
              {
                headers: {
                  "Content-Type": "application/json",
                  "session-token": sessionToken,
                },
              },
            );
          });

          // Wait for all findings to be updated
          await Promise.all(updateFindingsPromises);
        }
      }

      // Refresh the organization data to reflect the updated improvements
      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          true,
          true,
          false,
          false,
          false,
        ),
      );
      setTimeout(() => {
        resetForm();
      }, 0);
      handleOnClose(); // Close the modal after saving and fetching data
    } catch (error) {
      console.error("Error saving improvements data:", error);
    } finally {
      setLoading(false); // Reset loading state
    }
  };

  const statusOptions = [
    { label: "Open", value: "Open" },
    { label: "In Progress", value: "In Progress" },
    { label: "In Review", value: "In Review" },
    { label: "Done", value: "Done" },
  ];

  const truncateText = (text, maxLength) => {
    if (!text) {
      // If text is null, undefined, or an empty string
      return "There is no description provided";
    }

    if (text.length <= maxLength) {
      return text;
    }

    return (
      <>
        {text.substring(0, maxLength)}
        <span className={styles.readMore}>...</span>
      </>
    );
  };

  const handleNavigationClick = (path) => {
    dispatch(hideModal());
    navigate(`/${orgId}/${path}`, { replace: true });
  };

  const safeLinkedData = Array.isArray(linkedData) ? linkedData : [];

  const linkedFindingsColumns = (navigate, orgId) => [
    {
      accessorKey: "key",
      header: "Key",
      size: 100,
      cell: (props) => {
        const key = props.getValue();

        if (!key) {
          return (
            <div style={{ color: "gray", fontStyle: "italic" }}>
              Key not available
            </div>
          );
        }

        return (
          <a
            href={`/${orgId}/findings/${key}`}
            onClick={(e) => {
              e.preventDefault(); // Don't reload the page
              e.stopPropagation(); // Don't trigger table row click
              handleLinkedModalOpen(`findings/${key}`);
            }}
            className={styles.link}
          >
            {key}
          </a>
        );
      },
    },
    {
      accessorKey: "description",
      header: "Description",
      size: 350,
      cell: (props) => <div>{truncateText(props.getValue(), 80)}</div>,
    },
    {
      accessorKey: "responsible",
      header: "Responsible",
      size: 200,
      cell: (props) => {
        return props.row.original.responsible ? (
          <div>{props.row.original.responsible}</div>
        ) : (
          <div>Not defined</div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 140,
      cell: (props) => {
        const status = props.getValue();
        let statusClassName = "";

        switch (status) {
          case "Open":
            statusClassName = styles.openStatus;
            break;
          case "In Progress":
            statusClassName = styles.inProgressStatus;
            break;
          case "In Review":
            statusClassName = styles.inReviewStatus;
            break;
          case "Done":
            statusClassName = styles.resolvedStatus;
            break;
          default:
            break;
        }

        return (
          <div className={classNames(styles.statusCell, statusClassName)}>
            {status}
          </div>
        );
      },
    },
  ];

  const validationSchema = Yup.object().shape({
    responsible: Yup.string().matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid email address",
    ),
    date: Yup.date().required("Date is required"),
  });

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setEditField(null); // Reset editField when Esc is pressed
    }
  };

  const isEditable =
    !improvementProjectsData?.private ||
    improvementProjectsData?.summary !== "Hidden";

  return (
    <>
      <Formik
        initialValues={{
          ...editableData,
          date: editableData.date ? new Date(editableData.date) : "",
        }}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
      >
        {({
          values,
          dirty,
          resetForm,
          setFieldValue,
          submitForm,
          isSubmitting,
        }) => {
          return (
            <>
              <Modal
                name="improvement_projects_modal"
                title={
                  <div className={styles.modalHeader}>
                    {modalHistory.length > 0 && (
                      <SecondaryButton
                        text="Go back"
                        onClick={handleGoBack}
                        className={styles.backButton}
                        icon={<BackArrow />}
                        iconPosition="left"
                      />
                    )}
                    <div className={styles.titleKey}>
                      {improvementProjectsData?.private && (
                        <LockIcon
                          className={styles.lockIcon}
                          title="This finding is private"
                        />
                      )}
                      {improvementProjectsData?.key}
                    </div>
                  </div>
                }
                width="95vw"
                height="95vh"
                onClose={() => handleOnClose(dirty, resetForm)}
                headerAction={
                  isEditable ? (
                    <Button
                      text={loading ? "Saving..." : "Save Changes"}
                      onClick={submitForm}
                      className={styles.submitButton}
                      disabled={!dirty || isSubmitting}
                    />
                  ) : null
                }
              >
                <div className={styles.content}>
                  <Form className={styles.formikContent}>
                    <div className={styles.header}>
                      <div className={styles.leftSide}>
                        <div className={styles.row}>
                          <div className={styles.subheading}>Name:</div>
                          <div className={styles.bold}>
                            {improvementProjectsData?.name}
                          </div>
                        </div>
                        <div className={styles.row}>
                          <div className={styles.title}>Date:</div>
                          {editField === "date" ? (
                            <Field name="date" component={DatePickerField} />
                          ) : (
                            <div className={styles.dateContainer}>
                              <div className={styles.bold}>
                                {formatDate(
                                  values.date || improvementProjectsData?.date,
                                )}
                              </div>
                              {isEditable && (
                                <EditIcon
                                  className={styles.editIcon}
                                  onClick={() => setEditField("date")}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={styles.rightSide}>
                        <div className={styles.row}>
                          <div className={styles.title}>Status:</div>
                          <div className={styles.statusContainer}>
                            {editField === "status" ? (
                              <Field
                                name="status"
                                component={Select}
                                options={statusOptions}
                                value={values.status}
                                onChange={(e) => {
                                  setFieldValue("status", e.target.value);
                                  handleFieldChange("status", e.target.value);
                                  setEditField(null);
                                }}
                              />
                            ) : (
                              <>
                                <div
                                  className={classNames(
                                    styles.statusCell,
                                    values.status === "In Progress"
                                      ? styles.inProgressStatus
                                      : values.status === "Closed"
                                        ? styles.closedStatus
                                        : values.status === "Done"
                                          ? styles.resolvedStatus
                                          : styles.openStatus,
                                  )}
                                >
                                  {values.status}
                                </div>
                                {isEditable && (
                                  <EditIcon
                                    className={styles.editIcon}
                                    onClick={() => setEditField("status")}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        {!isSidebarOpen && (
                          <SecondaryButton
                            text="See Updates"
                            onClick={() => setSidebarOpen(true)}
                            className={styles.toggleSidebarButton}
                            type="Button"
                          />
                        )}
                      </div>
                    </div>
                    <Separator />

                    {(user?.Email === improvementProjectsData?.responsible ||
                      user?.Role === "QMSAdmin" ||
                      user?.Role === "Consultant") && (
                      <>
                        <div className={styles.row}>
                          <div className={styles.title}>Privacy:</div>
                          {editField === "private" ? (
                            <IconSelect
                              name="private"
                              options={[
                                {
                                  label: "Private Improvement Project",
                                  value: true,
                                  icon: ClosedLockIcon,
                                },
                                {
                                  label: "Public Improvement Project",
                                  value: false,
                                  icon: PublicIcon,
                                },
                              ]}
                              placeholder="Select privacy"
                            />
                          ) : (
                            <div className={styles.privacyDisplay}>
                              <img
                                src={
                                  values.private ? ClosedLockIcon : PublicIcon
                                }
                                alt={values.private ? "Private" : "Public"}
                                className={styles.privacyIcon}
                              />
                              <span>
                                {values.private
                                  ? "Private Improvement Project"
                                  : "Public Improvement Project"}
                              </span>
                              {isEditable && (
                                <EditIcon
                                  className={styles.editIcon}
                                  onClick={() => setEditField("private")}
                                />
                              )}
                            </div>
                          )}
                        </div>
                        <Separator />
                      </>
                    )}
                    {linkedRisks.length > 0 && (
                      <>
                        <div className={styles.title}>Linked Risks</div>
                        <div className={styles.linkedRisksWrapper}>
                          {linkedRisks.map((risk, index) => (
                            <span
                              key={risk.risk_id}
                              className={styles.riskItem}
                            >
                              <span className={styles.riskText}>
                                {risk.risk_id} -{" "}
                                {risk.qms_description || risk.description}
                              </span>{" "}
                              <span
                                className={classNames(
                                  styles.levelBadge,
                                  risk.level === "high"
                                    ? styles.highLevel
                                    : risk.level === "medium"
                                      ? styles.mediumLevel
                                      : risk.level === "low"
                                        ? styles.lowLevel
                                        : styles.unknownLevel,
                                )}
                              >
                                {risk.level}
                              </span>
                            </span>
                          ))}
                        </div>
                        <Separator />
                      </>
                    )}
                    <div className={styles.tableContainer}>
                      <table className={styles.teamRolesTable}>
                        <tbody>
                          {/* Responsible */}
                          <tr>
                            <td>Responsible</td>
                            <td>
                              {editField === "responsible" ? (
                                <Field
                                  name="responsible"
                                  component={Input}
                                  placeholder="Enter responsible email"
                                />
                              ) : (
                                <div>
                                  {Array.isArray(values.responsible)
                                    ? values.responsible.join(", ")
                                    : values.responsible || ""}
                                </div>
                              )}
                            </td>
                            <td>
                              {isEditable && (
                                <EditIcon
                                  className={styles.editIcon}
                                  onClick={() => setEditField("responsible")}
                                />
                              )}
                            </td>
                          </tr>
                          {/* Accountable */}
                          <tr>
                            <td>Accountable</td>
                            <td>
                              {editField === "accountable" ? (
                                <Field
                                  name="accountable"
                                  component={Pill}
                                  placeholder="Enter accountable emails"
                                  limit={5}
                                />
                              ) : (
                                (values.accountable || []).map(
                                  (email, index) => (
                                    <div key={index}>{email}</div>
                                  ),
                                ) || "Not defined"
                              )}
                            </td>
                            <td>
                              {isEditable && (
                                <EditIcon
                                  className={styles.editIcon}
                                  onClick={() => setEditField("accountable")}
                                />
                              )}
                            </td>
                          </tr>
                          {/* Consulted */}
                          <tr>
                            <td>Consulted</td>
                            <td>
                              {editField === "consulted" ? (
                                <Field
                                  name="consulted"
                                  component={Pill}
                                  placeholder="Enter consulted emails"
                                  limit={5}
                                />
                              ) : (
                                (values.consulted || []).map((email, index) => (
                                  <div key={index}>{email}</div>
                                )) || "Not defined"
                              )}
                            </td>
                            <td>
                              {isEditable && (
                                <EditIcon
                                  className={styles.editIcon}
                                  onClick={() => setEditField("consulted")}
                                />
                              )}
                            </td>
                          </tr>
                          {/* Informed */}
                          <tr>
                            <td>Informed</td>
                            <td>
                              {editField === "informed" ? (
                                <Field
                                  name="informed"
                                  component={Pill}
                                  placeholder="Enter informed emails"
                                  limit={5}
                                />
                              ) : (
                                (values.informed || []).map((email, index) => (
                                  <div key={index}>{email}</div>
                                )) || "Not defined"
                              )}
                            </td>
                            <td>
                              {isEditable && (
                                <EditIcon
                                  className={styles.editIcon}
                                  onClick={() => setEditField("informed")}
                                />
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <Separator />
                    {improvementProjectsData?.status === "Done" && (
                      <>
                        <div className={styles.title}>Summary:</div>
                        {editField === "summary" ? (
                          <div className={styles.editorContainer}>
                            <Field
                              name="summary"
                              component={WYSIWYGEditor}
                              placeholder="Edit the summary..."
                            />
                          </div>
                        ) : (
                          <div className={styles.summaryContent}>
                            {isEditable && (
                              <EditIcon
                                className={styles.editIcon}
                                onClick={() => setEditField("summary")} // Enable editing
                              />
                            )}
                            {values.summary && (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(
                                    marked.parse(values.summary.trim()),
                                  ),
                                }}
                              />
                            )}
                          </div>
                        )}
                        <Separator />
                      </>
                    )}
                    {/* Description Field */}
                    <div className={styles.subheading}>Description:</div>
                    {editField === "description" ? (
                      <Field
                        name="description"
                        component={Textarea}
                        value={stripHtmlTags(
                          values?.description ||
                            improvementProjectsData?.description,
                        )} // Strip HTML tags when editing
                      />
                    ) : (
                      <div className={styles.descriptionWrapper}>
                        <div
                          className={styles.description}
                          dangerouslySetInnerHTML={{
                            __html: (
                              values?.description ||
                              improvementProjectsData?.description ||
                              ""
                            ).replace(/\n/g, "<br />"), // Replace new lines with <br />
                          }}
                        />
                        {isEditable && (
                          <EditIcon
                            className={styles.editIcon}
                            onClick={() => setEditField("description")}
                          />
                        )}
                      </div>
                    )}
                    {linkedData?.linked_findings?.length > 0 && (
                      <>
                        <Separator />
                        <div className={styles.title}>Linked Findings</div>
                        <div className={styles.tableContainer}>
                          <Table
                            columns={linkedFindingsColumns(navigate, orgId)}
                            data={linkedData?.linked_findings}
                          />
                        </div>
                        <SecondaryButton
                          text="View all findings..."
                          className={styles.secondaryButton}
                          onClick={() => handleNavigationClick("findings")}
                        />
                      </>
                    )}
                  </Form>
                  <ModalSideBar
                    isOpen={isSidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    updates={updates}
                    setUpdates={setUpdates}
                    improvementId={improvementProjectsData?.id}
                  />
                </div>
              </Modal>
              {showConfirmationPopup && (
                <ConfirmationPopup
                  message="Your changes won’t be saved if you close this window."
                  illustration={WarningIllustration}
                  confirmText="Close Without Saving"
                  onConfirm={() => handleConfirmClose(resetForm)}
                  onCancel={handleCancel}
                />
              )}
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default ImprovementsProjectsModal;
